/**
 * Live V6 draft — two personas on one stage.
 *
 * Write act: the teacher's words stream in (cyan caret, subtle unverified
 * style). Edit act: the curator takes over — the accuracy checklist opens
 * above the draft (amber caret below) and its rows check off one by one,
 * skipped steps included, while semantic patches perform on screen. The
 * checklist shrinks and fades when the work completes, a verification
 * receipt lands, and the `complete` body typesets over everything (ground
 * truth, never this draft).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@state/chat';
import { useEditTheater } from './use-edit-theater';
import type { Decoration } from './use-edit-theater';
import { AccuracyChecklist } from './accuracy-checklist';
import { VerificationReceipt } from './verification-receipt';
import { glideToTop } from './scroll-glide';

interface ChatDraftProps {
  message: ChatMessage;
  onTheaterDone: () => void;
}

/** Split the doc into plain/decorated segments for rendering. */
function segments(doc: string, marks: Decoration[]) {
  if (marks.length === 0) return [{ text: doc, cls: '' }];
  const sorted = [...marks].sort((a, b) => a.start - b.start);
  const out: { text: string; cls: string }[] = [];
  let pos = 0;
  for (const m of sorted) {
    const start = Math.max(0, Math.min(m.start, doc.length));
    const end = Math.max(start, Math.min(m.end, doc.length));
    if (start > pos) out.push({ text: doc.slice(pos, start), cls: '' });
    out.push({ text: doc.slice(start, end), cls: m.cls });
    pos = end;
  }
  if (pos < doc.length) out.push({ text: doc.slice(pos), cls: '' });
  return out;
}

export function ChatDraft({ message, onTheaterDone }: ChatDraftProps) {
  const { doc, marks, showReceipt, skip, skipped, actStates, checklistCollapsed } = useEditTheater(
    message,
    onTheaterDone,
  );
  // The reader's manual toggle wins over the show's automatic fold-up.
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const inTheater = Boolean(message.editBaseline);
  const text = inTheater ? doc : (message.streamingText ?? '');

  // One glide up per generation, to the very top of the panel — the same
  // place completion lands, so the reader gets one move, not two. Gliding to
  // the top of the stage instead parked the raw draft under the panel header
  // with the study card scrolled off (Brian, UAT 2026-09-21).
  const glidedRef = useRef(false);
  const glideUp = useCallback(() => {
    if (glidedRef.current || !stageRef.current) return;
    const container = stageRef.current.closest('.chat-panel-messages') as HTMLElement | null;
    if (!container) return;
    glidedRef.current = true;
    glideToTop(container, { ms: 800 });
  }, []);

  // Follow the writing: keep the panel pinned to the bottom as characters
  // appear — but only when the reader is already near the bottom, so a
  // manual scroll-up to re-read is never fought. Once the end-of-stream
  // glide has begun, stop pinning entirely.
  useEffect(() => {
    if (glidedRef.current) return;
    const container = stageRef.current?.closest('.chat-panel-messages') as HTMLElement | null;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 160) container.scrollTop = container.scrollHeight;
  }, [text, showReceipt]);

  // Start the scroll-up RIGHT at the last token — don't wait for the
  // pipeline's step bookkeeping (it flips done only after post-generation
  // judging, seconds late). The quiet-window arms only after sustained
  // streaming (many updates, real length), so the model-spin-up stall
  // right after the opener can't trip it; and any misfire self-heals —
  // new tokens always re-arm the follow-the-writing pin.
  const steps = message.progressSteps;
  const generationDone = !!steps?.length && steps.every((s) => s.status === 'done');
  const updateCountRef = useRef(0);
  useEffect(() => {
    if (!text) {
      glidedRef.current = false;
      updateCountRef.current = 0;
      return;
    }
    if (inTheater) return;
    updateCountRef.current += 1;
    if (generationDone) {
      glideUp();
      return;
    }
    // Tokens still arriving: keep (or resume) following the writing.
    glidedRef.current = false;
    if (updateCountRef.current >= 8 && text.length > 200) {
      const timer = setTimeout(glideUp, 450);
      return () => clearTimeout(timer);
    }
  }, [text, inTheater, generationDone, glideUp]);

  // Fast edit-start (or a missed quiet window): glide when the show opens.
  useEffect(() => {
    if (inTheater) glideUp();
  }, [inTheater, glideUp]);

  if (!text && !inTheater) return null;

  return (
    <div className="chat-draft-stage" ref={stageRef}>
      {inTheater && (
        <AccuracyChecklist
          title="Checking against Scripture…"
          statusTestId="edit-theater-status"
          acts={actStates}
          collapsed={userCollapsed ?? checklistCollapsed}
          onToggle={() => setUserCollapsed(!(userCollapsed ?? checklistCollapsed))}
          trailing={
            !showReceipt && !skipped ? (
              <button type="button" className="edit-theater-skip" onClick={skip}>
                Skip
              </button>
            ) : undefined
          }
        />
      )}
      <div className="chat-draft chat-draft--unverified" data-testid="chat-draft">
        {segments(text, inTheater ? marks : []).map((s, i) =>
          s.cls ? <span key={i} className={`edit-mark ${s.cls}`}>{s.text}</span> : <span key={i}>{s.text}</span>,
        )}
        <span
          className={`chat-draft-caret${inTheater ? ' chat-draft-caret--curator' : ''}`}
          aria-hidden="true"
        />
      </div>
      {showReceipt && message.editReceipt && message.editReceipt.length > 0 && (
        <VerificationReceipt items={message.editReceipt} />
      )}
    </div>
  );
}
